import { useMemo, useEffect } from "react";
import {
  Chart,
  formatNumber,
  getColorFromCssVariable,
  Combobox,
  OverflowText,
} from "ui";
import { ChevronDownIcon } from "icons/outline";
import { ChartCard } from "../ChartCard";
import { useErrorCodeFilter } from "../DatadogAnalytics/useErrorCodeFilter";
import { useDatadogQuery } from "../DatadogAnalytics/useDatadogQuery";
import { percentageFormatter } from "../DatadogAnalytics/utils";
import { useDeclineTypeFilter } from "../DatadogAnalytics/useDeclineTypeFilter";
import { useMedia } from "react-use";

type DeclineReasonsData = {
  label: string;
  value: number;
  type: "SOFT_DECLINE" | "HARD_DECLINE";
}[];

const ERROR_CODE_COLOR_VARS = [
  "--chart-categorical-6-subtler",
  "--chart-categorical-7-subtler",
  "--chart-categorical-5-subtler",
  "--chart-categorical-4-subtler",
];
const OTHERS_COLOR_VAR = "--chart-categorical-8-subtler";

export function DeclineReasonsChart() {
  const { ref, queryStatus, data, error, refetch } =
    useDatadogQuery<DeclineReasonsData>({
      chartType: "ERROR_CODES_COUNTS",
      delayMs: 1800,
    });

  const { selectedType, setSelectedType, typeOptions } = useDeclineTypeFilter();
  const selectedDeclineType = selectedType[0];

  const isMobile = useMedia("(max-width: 767px)", false);

  const allDeclineReasons = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];

    const filteredData =
      selectedDeclineType === "ALL_TYPES"
        ? data
        : data.filter((item) => item.type === selectedDeclineType);

    return filteredData;
  }, [data, selectedDeclineType]);

  const {
    selectedErrorCodes,
    setSelectedErrorCodes,
    errorCodeOptions,
    filteredData,
    isItemDisabled,
  } = useErrorCodeFilter(allDeclineReasons);

  // Reset error codes when decline type changes
  useEffect(() => {
    setSelectedErrorCodes([]);
  }, [selectedDeclineType, setSelectedErrorCodes]);

  // Prepare data for doughnut chart
  const { labels, chartData, colors, total } = useMemo(() => {
    const labels = filteredData.map((item) => item.label);
    const chartData = filteredData.map((item) => item.value);
    const total = chartData.reduce((sum, value) => sum + value, 0);

    const colors = filteredData.map((item, index) => {
      if (item.label === "OTHERS")
        return getColorFromCssVariable(OTHERS_COLOR_VAR);

      return getColorFromCssVariable(ERROR_CODE_COLOR_VARS[index % 4]);
    });

    return { labels, chartData, colors, total };
  }, [filteredData]);

  return (
    <ChartCard
      ref={ref}
      title="Top codes by volume"
      description="Breakdown of failed payment attempts by MoneyHash unified error code."
      contentClassName="items-center justify-center h-auto min-h-80 overflow-hidden"
      queryStatus={queryStatus}
      error={error}
      retry={refetch}
      headerAction={
        <div className="flex gap-2">
          <Combobox
            mode="popup"
            label={selectedType[1]}
            options={typeOptions}
            selected={selectedType}
            onSelect={(option) => {
              if (option) setSelectedType(option);
            }}
            className="w-auto"
            align="start"
          />
          <Combobox
            mode="popup"
            label="All error codes"
            placeholder="Search error codes"
            options={errorCodeOptions}
            selected={selectedErrorCodes}
            onSelect={setSelectedErrorCodes}
            multiple
            withConfirmation
            className="w-40"
            isItemDisabled={isItemDisabled}
            align="start"
            disableSelectAll
            renderTrigger={(triggerProps) => (
              <button type="button" {...triggerProps}>
                <span className="min-w-0 flex-1 truncate text-left text-foundations-bolder">
                  {selectedErrorCodes.length > 0
                    ? `${selectedErrorCodes.length} error${
                        selectedErrorCodes.length > 1 ? "s" : ""
                      } selected`
                    : "All error codes"}
                </span>
                <ChevronDownIcon className="ml-2 size-5 shrink-0 text-foundations-subtle transition-transform group-data-[popup-open]:rotate-180" />
              </button>
            )}
            renderOption={(label) => (
              <OverflowText variant="light">{label}</OverflowText>
            )}
            popupClassName="w-72"
          />
        </div>
      }
    >
      <Chart
        key={`${selectedDeclineType}-${filteredData.length}-${total}`}
        type="doughnut"
        labels={labels}
        datasets={[
          {
            data: chartData,
            backgroundColor: colors,
            borderColor: getColorFromCssVariable(
              "--border-foundations-subtler"
            ),
            hoverBorderColor: getColorFromCssVariable(
              "--border-foundations-subtle"
            ),
            borderWidth: 2,
          },
        ]}
        className="flex flex-col-reverse items-center md:flex-row md:gap-12"
      >
        <Chart.Canvas
          className="size-56 max-w-full shrink-0"
          centerText={{
            text: formatNumber({ value: total, compact: true }),
          }}
        />
        <Chart.Legend
          key={`${isMobile}`}
          variant={isMobile ? "row" : "column"}
          className="overflow-hidden"
          labelFormatter={(label) => (
            <OverflowText
              className="block max-w-64 sm:max-w-none lg:max-w-64 2xl:max-w-none"
              size="sm"
              variant="light"
              render={<span />}
            >
              {label}
            </OverflowText>
          )}
          descriptionFormatter={(_, index) => (
            <>
              {percentageFormatter((chartData[index] / total) * 100)}%{" "}
              <span className="text-foundations-subtle">
                ({formatNumber({ value: chartData[index], compact: true })})
              </span>
            </>
          )}
        />
        <Chart.Tooltip
          variant="circle"
          valueFormatter={(value, dataIndex) => (
            <>
              {formatNumber({ value, compact: true })}{" "}
              <span className="text-foundations-subtle">
                ({percentageFormatter((chartData[dataIndex] / total) * 100)}%)
              </span>
            </>
          )}
        />
      </Chart>
    </ChartCard>
  );
}
